import React, {useEffect, useMemo, useRef, useState} from "react"
import {CardContent, TextField} from "@mui/material";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {clearProfilesMessages, createEntity, updateEntity} from './ProfilesReducer';
import {useSnackbar} from "notistack";
import {RootState} from "../../../Config/Store";
import {useAppDispatch, useAppSelector} from "../../../Config/Hooks";
import {ACard, ADataGrid, ADataGridFilter, PagedSearchParams} from "@atiautomacao/ati-ui-library";
import {searchEntitiesData} from "./AuthorityReducer";
import {extractFiltersFromColumns} from "../../../Utils/DataUitils";
import {columnsData} from "./columnsData";
import {SystemRoutes} from "../../../Utils/RouteUtils";
import {HeaderMenu} from "../HeaderMenu";
import {
    mapCatalogToRows,
    mergeVisibleSelection,
    RowAuthority,
    selectedAuthorityIdsFromProfile,
} from "./profilesSaveSelection";

const ProfilesSaveSettingsPage = () => {
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const selectedId = location.state?.id;
    const navigate = useNavigate();
    const params = useParams();
    const [isNew] = useState(params.id === undefined);
    const [inputName, setInputName] = useState<string>("");
    const [isClear, setIsClear] = useState<boolean>(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const authorityEntities = useAppSelector((state : RootState) => state.authority.entities);
    const totalOfRecords = useAppSelector((state : RootState) => state.authority.totalOfRecords);
    const [pagedSearchParams, setPagedSearchParams] = useState(new PagedSearchParams(extractFiltersFromColumns(columnsData), 0, 1000));
    const entities = useAppSelector((state : RootState) => state.profile.entities);
    const authorityByIdRef = useRef<Map<number, any>>(new Map());
    const initializedForProfileId = useRef<string | number | null>(null);
    const applyingSelectionRef = useRef(false);

    const selectedProfile = useMemo(() => {
        if (!selectedId) {
            return undefined;
        }
        return entities.find((profile) => Number(profile.id) === Number(selectedId));
    }, [selectedId, entities]);

    const [rows, setRows] = useState<Array<RowAuthority>>([]);

    useEffect(() => {
        if (!selectedId) {
            initializedForProfileId.current = null;
            setSelectedIds([]);
            setInputName("");
            return;
        }
        if (!selectedProfile) {
            return;
        }
        if (initializedForProfileId.current == selectedId) {
            return;
        }
        initializedForProfileId.current = selectedId;
        setInputName(selectedProfile.name ?? "");
        setSelectedIds(selectedAuthorityIdsFromProfile(selectedProfile.authorityGroupItemDTOList));
    }, [selectedId, selectedProfile]);

    useEffect(() => {
        for (const authority of authorityEntities) {
            authorityByIdRef.current.set(Number(authority.id), authority);
        }
        applyingSelectionRef.current = true;
        setRows(mapCatalogToRows(authorityEntities, selectedIds));
        setIsClear(true);
        const timeoutId = window.setTimeout(() => {
            applyingSelectionRef.current = false;
            setIsClear(false);
        }, 0);
        return () => window.clearTimeout(timeoutId);
    }, [authorityEntities, selectedIds]);

    const isSaving = useAppSelector((state : RootState) => state.profile.saving);
    const errorMessage = useAppSelector((state : RootState) => state.profile.errorMessage);

    const toSaveItems = (ids: number[]) => ids.map((id) => {
        const authority = authorityByIdRef.current.get(Number(id));
        return {
            id: null,
            authorityGroup: null,
            authority: {
                id,
                name: authority?.name,
                displayName: authority?.displayName,
                domainDisplayName: authority?.domain?.displayName
            }
        };
    });

    const saveEntity = () => {
        if(selectedIds.length < 2) {
            enqueueSnackbar("É necessário selecionar pelo menos 2 permissões", {
                variant: 'error',
            });
            return
        }

        if (selectedId) {
            dispatch(updateEntity(
                {
                    id: Number(selectedId),
                    name: inputName,
                    authorityGroupItemDTOList: toSaveItems(selectedIds)
                }
            )).then(success => {
                return navigate("/settings/profiles");
            }).catch(error => {
                enqueueSnackbar(errorMessage, {
                    variant: 'error',
                });
                dispatch(clearProfilesMessages());
            });
        } else {
            dispatch(createEntity(
                {
                    id: null,
                    name: inputName,
                    authorityGroupItemDTOList: toSaveItems(selectedIds)
                }
            )).then(success => {
                return navigate("/settings/profiles");
            }).catch(error => {
                enqueueSnackbar(errorMessage, {
                    variant: 'error',
                });
                dispatch(clearProfilesMessages());
            });
        }
    };


    useEffect(() => {
        if (!isSaving) {
            dispatch(searchEntitiesData(pagedSearchParams));
        }
    }, [pagedSearchParams]);

    const handleOnFiltersChange = (dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
        let newPageParams :PagedSearchParams = new PagedSearchParams(dataGridFilters, page, pageSize);
        setPagedSearchParams(
            newPageParams
        );
    };

    const handleOnRowSelect = (rowsSelected : any) => {
        if (applyingSelectionRef.current) {
            return;
        }
        const visibleIds = rows.map((row) => Number(row.id));
        const visibleSelectedIds = (rowsSelected ?? []).map((row: {id: number}) => Number(row.id));
        setSelectedIds((previousIds) => {
            const nextIds = mergeVisibleSelection(previousIds, visibleIds, visibleSelectedIds);
            if (
                nextIds.length === previousIds.length &&
                nextIds.every((id) => previousIds.includes(id))
            ) {
                return previousIds;
            }
            return nextIds;
        });
    };

    return (
        <>
            <HeaderMenu isOnSave={true} handleSave={saveEntity} systemRoutes={SystemRoutes.SETTINGS_PROFILE}/>
            <ACard>
                    <CardContent>
                        <React.Fragment>
                            <TextField
                                label="PERFIL"
                                variant="outlined"
                                fullWidth
                                size="small"
                                margin="normal"
                                value={inputName}
                                onChange={(e) => setInputName(e.target.value)}
                                placeholder={isNew ? "Insira o nome do perfil" : ""}
                            />
                            <ADataGrid
                                ariaLabel={"settings-profiles-form-grid"}
                                columns={columnsData}
                                rows={rows}
                                loading={false}
                                totalOfRecords={totalOfRecords}
                                page={pagedSearchParams.page}
                                onClearSelectedTempRows={isClear}
                                disablePagination={true}
                                rowsPerPage={pagedSearchParams.size}
                                onFiltersChange={handleOnFiltersChange}
                                onRowSelect={handleOnRowSelect}
                            />
                        </React.Fragment>
                    </CardContent>
                </ACard>
        </>
    );

}

export default ProfilesSaveSettingsPage;
export type {RowAuthority};
